// @ts-nocheck
/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import {
  listLabUploads,
  saveLabUpload,
  deleteLabUpload,
  getLabFileUrl,
} from "@/lib/labs.functions";
import { extractLabValues } from "@/lib/lab-extract.functions";
import { LAB_PROVIDERS, LAB_PROVIDER_DISCLAIMER } from "./labProviders";
import { useLabUploads } from "./labs";


// Match the palette used elsewhere in KnowMenoApp.
const T = "#FFC1CD", TL = "#FFE4EA", TD = "#4B1F4F";
const RO = "#FF5C7A", RL = "#FFE4EA";
const GR = "#6E4B71", GL = "#FAF7F5", GD = "#6E4B71";
const WH = "#ffffff";

const MARKERS = [
  { key: "estradiol",    label: "Estradiol",    defaultUnit: "pg/mL" },
  { key: "fsh",          label: "FSH",          defaultUnit: "mIU/mL" },
  { key: "lh",           label: "LH",           defaultUnit: "mIU/mL" },
  { key: "progesterone", label: "Progesterone", defaultUnit: "ng/mL" },
  { key: "tsh",          label: "TSH",          defaultUnit: "µIU/mL" },
] as const;

type Marker = (typeof MARKERS)[number]["key"];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      resolve(result.split(",")[1] || "");
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

async function requireAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error("You're signed out. Please sign in again.");
  return accessToken;
}

type DraftValues = {
  lab_date: string;
  lab_name: string;
  values: Record<Marker, string>;
  units: Record<Marker, string>;
  file_path: string | null;
  file_mime_type: string | null;
};

function emptyDraft(): DraftValues {
  return {
    lab_date: todayIso(),
    lab_name: "",
    values: { estradiol: "", fsh: "", lh: "", progesterone: "", tsh: "" },
    units: Object.fromEntries(MARKERS.map(m => [m.key, m.defaultUnit])) as Record<Marker, string>,
    file_path: null,
    file_mime_type: null,
  };
}

export default function LabUploads({ onGoToInsights }: { onGoToInsights?: (sectionId?: string) => void } = {}) {
  const { rows: fetchedRows, loading: fetchLoading } = useLabUploads();
  const save = useServerFn(saveLabUpload);
  const del = useServerFn(deleteLabUpload);
  const getUrl = useServerFn(getLabFileUrl);
  const extract = useServerFn(extractLabValues);

  const fileRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftValues | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null); // row id being deleted/opened

  // Mirror the hook's fetched rows into local state so existing optimistic
  // mutations after save/delete continue to work unchanged.
  useEffect(() => {
    if (!fetchLoading) {
      setRows(fetchedRows as any[]);
      setLoaded(true);
    }
  }, [fetchLoading, fetchedRows]);


  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-selecting the same file later
    if (file.size > 10 * 1024 * 1024) {
      setError("That file is over 10 MB. Please upload a smaller PDF or photo.");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      // 1. Upload original to private storage first.
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("You're signed out. Please sign in again.");
      const accessToken = await requireAccessToken();
      const ext = (file.name.split(".").pop() || "bin").toLowerCase().replace(/[^a-z0-9]/g, "") || "bin";
      const path = `${user.id}/${Date.now()}-${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("lab-uploads")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw new Error(upErr.message);

      // 2. Extract values via AI.
      const b64 = await fileToBase64(file);
      const extracted = await extract({ data: { accessToken, fileBase64: b64, mimeType: file.type || "application/pdf" } });

      // 3. Open editable draft pre-filled with extracted values.
      const d = emptyDraft();
      d.file_path = path;
      d.file_mime_type = file.type || null;
      if (extracted.lab_date) d.lab_date = extracted.lab_date;
      if (extracted.lab_name) d.lab_name = extracted.lab_name;
      for (const m of MARKERS) {
        const v = (extracted as any)[`${m.key}_value`];
        const u = (extracted as any)[`${m.key}_unit`];
        if (v !== null && v !== undefined) d.values[m.key] = String(v);
        if (u) d.units[m.key] = u;
      }
      setDraft(d);
    } catch (err: any) {
      setError(err?.message || "We couldn't process that file. Try a clearer PDF or photo.");
    } finally {
      setUploading(false);
    }
  };

  const handleManualAdd = () => {
    setError(null);
    setDraft(emptyDraft());
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.lab_date) { setError("Please pick the lab draw date."); return; }
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        accessToken: await requireAccessToken(),
        lab_date: draft.lab_date,
        lab_name: draft.lab_name.trim() || null,
        file_path: draft.file_path,
        file_mime_type: draft.file_mime_type,
      };
      for (const m of MARKERS) {
        const raw = draft.values[m.key].trim();
        const num = raw === "" ? null : Number(raw);
        payload[`${m.key}_value`] = raw === "" || Number.isNaN(num) ? null : num;
        payload[`${m.key}_unit`] = draft.units[m.key] || m.defaultUnit;
      }
      const inserted = await save({ data: payload });
      setRows(prev => [inserted, ...prev].sort((a, b) => (a.lab_date < b.lab_date ? 1 : -1)));
      setDraft(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't save your lab values.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this lab upload? This can't be undone.")) return;
    setBusy(id);
    try {
      const accessToken = await requireAccessToken();
      await del({ data: { id, accessToken } });
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      setError(err?.message || "Couldn't delete this upload.");
    } finally {
      setBusy(null);
    }
  };

  const handleOpen = async (id: string) => {
    setBusy(id);
    try {
      const accessToken = await requireAccessToken();
      const { url } = await getUrl({ data: { id, accessToken } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      setError(err?.message || "Couldn't open the original file.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFile}
        style={{ display: "none" }}
      />

      {/* Header card with upload + manual entry */}
      <div style={{ background: WH, borderRadius: 20, padding: "22px 20px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "#FFD0D9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>🧬</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, color: GD, marginBottom: 2, letterSpacing: "-0.01em" }}>Upload your lab results</div>
            <div style={{ fontSize: 12, color: GR, lineHeight: 1.55 }}>We'll read your hormone values so they appear in your Symptom Intelligence Report.</div>
          </div>
        </div>

        {onGoToInsights && (
          <button
            type="button"
            onClick={() => onGoToInsights("lab-trends")}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "#4B1F4F",
              fontWeight: 600,
              fontSize: 13,
              textDecoration: "underline",
              cursor: "pointer",
              fontFamily: "inherit",
              marginTop: 4,
              marginBottom: 12,
              textAlign: "left",
              display: "block",
            }}
          >
            View trends in Insights →
          </button>
        )}


        {error && (
          <div style={{ background: "#FFD0D9", borderRadius: 12, padding: "10px 13px", marginBottom: 12, fontSize: 12, color: RO, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !!draft}
          style={{
            width: "100%",
            padding: 14,
            borderRadius: 14,
            background: uploading || draft ? "#FAF7F5" : "#FF5C7A",
            color: uploading || draft ? GR : WH,
            border: "none",
            fontSize: 14,
            fontWeight: 500,
            cursor: uploading || draft ? "not-allowed" : "pointer",
            marginBottom: 8,
          }}
        >
          {uploading ? "Reading your results…" : "Upload PDF or photo"}
        </button>
        <button
          onClick={handleManualAdd}
          disabled={!!draft}
          style={{
            width: "100%",
            padding: 11,
            borderRadius: 12,
            background: WH,
            color: draft ? GR : TD,
            border: `1px solid ${draft ? "#E8D5DA" : "#FFB0BD"}`,
            fontSize: 13,
            fontWeight: 500,
            cursor: draft ? "not-allowed" : "pointer",
          }}
        >
          + Enter values manually
        </button>
      </div>

      {/* Editable confirm form */}
      {draft && (
        <div style={{ background: WH, borderRadius: 20, padding: "20px", marginBottom: 16, border: `1.5px solid #FFB0BD` }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: TD, marginBottom: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {draft.file_path ? "Confirm extracted values" : "Enter values"}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, color: GR, display: "block", marginBottom: 4 }}>Lab draw date *</label>
              <input
                type="date"
                value={draft.lab_date}
                onChange={(e) => setDraft({ ...draft, lab_date: e.target.value })}
                max={todayIso()}
                style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid #E8D5DA", fontSize: 13, color: GD, background: WH }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: GR, display: "block", marginBottom: 4 }}>Lab name (optional)</label>
              <input
                type="text"
                value={draft.lab_name}
                placeholder="e.g. Quest Diagnostics"
                onChange={(e) => setDraft({ ...draft, lab_name: e.target.value.slice(0, 120) })}
                style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid #E8D5DA", fontSize: 13, color: GD, background: WH }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginBottom: 14 }}>
            {MARKERS.map((m) => (
              <div key={m.key} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, alignItems: "center" }}>
                <div style={{ fontSize: 13, color: GD, fontWeight: 500 }}>{m.label}</div>
                <input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={draft.values[m.key]}
                  placeholder="—"
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      values: { ...draft.values, [m.key]: e.target.value.slice(0, 20) },
                    })
                  }
                  style={{ padding: "9px 10px", borderRadius: 10, border: "1px solid #E8D5DA", fontSize: 13, color: GD, background: WH, width: "100%" }}
                />
                <input
                  type="text"
                  value={draft.units[m.key]}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      units: { ...draft.units, [m.key]: e.target.value.slice(0, 20) },
                    })
                  }
                  style={{ padding: "9px 10px", borderRadius: 10, border: "1px solid #E8D5DA", fontSize: 12, color: GR, background: "#FAF7F5", width: "100%" }}
                />
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11, color: GR, lineHeight: 1.5, marginBottom: 12 }}>
            Edit anything that looks wrong. Leave a marker blank if it wasn't on this report.
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => { setDraft(null); setError(null); }}
              disabled={saving}
              style={{ flex: 1, padding: 12, borderRadius: 12, background: GL, color: GD, border: "none", fontSize: 13, fontWeight: 500, cursor: saving ? "wait" : "pointer" }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ flex: 2, padding: 12, borderRadius: 12, background: "#FF5C7A", color: WH, border: "none", fontSize: 14, fontWeight: 500, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Saving…" : "Save lab results"}
            </button>
          </div>
        </div>
      )}

      {/* Saved list */}
      {loaded && rows.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: GR, marginBottom: 8, letterSpacing: "0.06em", textTransform: "uppercase", paddingLeft: 4 }}>
            Your saved lab results · {rows.length}
          </div>
          {rows.map((row) => (
            <div key={row.id} style={{ background: WH, borderRadius: 16, padding: "14px 16px", marginBottom: 10, border: "0.5px solid #E8D5DA" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: GD }}>
                    {new Date(row.lab_date + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                  </div>
                  {row.lab_name && <div style={{ fontSize: 11, color: GR, marginTop: 2 }}>{row.lab_name}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {row.file_path && (
                    <button
                      onClick={() => handleOpen(row.id)}
                      disabled={busy === row.id}
                      style={{ background: GL, border: "none", borderRadius: 10, padding: "6px 10px", fontSize: 11, color: GD, cursor: "pointer" }}
                    >
                      View original
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(row.id)}
                    disabled={busy === row.id}
                    style={{ background: "#FFD0D9", border: "none", borderRadius: 10, padding: "6px 10px", fontSize: 11, color: RO, cursor: "pointer" }}
                  >
                    {busy === row.id ? "…" : "Delete"}
                  </button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {MARKERS.map((m) => {
                  const v = row[`${m.key}_value`];
                  const u = row[`${m.key}_unit`];
                  return (
                    <div key={m.key} style={{ background: "#FAF7F5", borderRadius: 10, padding: "8px 10px" }}>
                      <div style={{ fontSize: 10, color: GR, marginBottom: 2 }}>{m.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: v == null ? GR : GD }}>
                        {v == null ? "—" : `${v} ${u || ""}`.trim()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div style={{ background: "#FFD0D9", borderRadius: 12, padding: "10px 13px", fontSize: 11, color: TD, lineHeight: 1.55 }}>
            ✓ These values appear in your Symptom Intelligence Report's Lab Values section.
          </div>
        </div>
      )}

      {loaded && rows.length === 0 && !draft && (
        <div style={{ background: "#FAF7F5", borderRadius: 14, padding: "14px 16px", fontSize: 12, color: GR, lineHeight: 1.6, marginBottom: 16 }}>
          No lab results saved yet. Upload a PDF, snap a photo, or enter values by hand — everything you save will appear in your Symptom Intelligence Report.
        </div>
      )}

      {/* Order new tests — affiliate links section */}
      <div style={{ background: WH, borderRadius: 20, padding: "20px 18px", border: "0.5px solid #E8D5DA" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <div style={{ width: 36, height: 36, borderRadius: 11, background: "#FFD0D9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🧪</div>
          <div style={{ fontSize: 15, fontWeight: 500, color: GD }}>Don't have recent labs? Order at home</div>
        </div>
        <div style={{ fontSize: 12, color: GR, lineHeight: 1.55, marginBottom: 14, paddingLeft: 48 }}>
          At-home blood spot kits that test the same hormones we track here.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {LAB_PROVIDERS.map((p) => (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                textDecoration: "none",
                background: "#FFE4EA",
                borderRadius: 12,
                padding: "12px 14px",
                border: "0.5px solid #FAF7F5",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: GD }}>{p.name}</div>
                <div style={{ fontSize: 11, color: "#FF5C7A", fontWeight: 500 }}>Visit site →</div>
              </div>
              <div style={{ fontSize: 12, color: TD, fontWeight: 500, marginBottom: 4 }}>{p.product}</div>
              <div style={{ fontSize: 11, color: GR, lineHeight: 1.5 }}>{p.blurb}</div>
            </a>
          ))}
        </div>
        <div style={{ fontSize: 10, color: GR, lineHeight: 1.55, textAlign: "center" }}>
          {LAB_PROVIDER_DISCLAIMER}
        </div>
      </div>
    </div>
  );
}
